import React, { useState } from 'react';
import { 
    Mail, 
    Phone, 
    MapPin, 
    Linkedin, 
    Github, 
    Twitter, 
    Instagram,
    FileDown, 
    Send,
    CheckCircle,
    AlertTriangle
} from 'lucide-react';

import './Contact.css'

const Popup = ({ type, message, onClose }) => {
    return (
        <div className="popup-overlay">
            <div className={`popup-content ${type}`}>
                <div className="popup-icon">
                    {type === 'success' ? (
                        <CheckCircle size={40} />
                    ) : (
                        <AlertTriangle size={40} />
                    )}
                </div>
                <h3>{type === 'success' ? 'Success!' : 'Oops!'}</h3>
                <p>{message}</p>
                <button className="popup-close" onClick={onClose}>
                    {type === 'success' ? 'Great!' : 'Try Again'}
                </button>
            </div>
        </div>
    );
};

const Contact = () => {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        subject: '',
        message: ''
    });
    const [status, setStatus] = useState('');
    const [popup, setPopup] = useState({ show: false, type: '', message: '' });

    const funnySuccessMessages = [
        "Message sent faster than a caffeine-powered developer! 🚀",
        "Your message is now traveling through the digital cosmos! 🌌",
        "High five! Your message just made it to our inbox! ✋",
        "Success! Time to do a little victory dance! 💃",
    ];

    const funnyErrorMessages = [
        "Oops! Our hamsters powering the server need a break! 🐹",
        "Error 418: I'm a teapot... Just kidding, but something did go wrong! 🫖",
        "Houston, we have a problem! Message got lost in cyberspace! 🚀",
        "Seems like our carrier pigeons are on strike! 🐦",
    ];

    const showPopup = (type, customMessage = '') => {
        const messages = type === 'success' ? funnySuccessMessages : funnyErrorMessages;
        const message = customMessage || messages[Math.floor(Math.random() * messages.length)];
        setPopup({ show: true, type, message });
    };

    const validateForm = () => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        
        if (!emailRegex.test(formData.email)) {
            showPopup('error', "That email looks suspicious! 🕵️‍♂️ Please double-check it!");
            return false;
        }

        if (formData.message.length < 10) {
            showPopup('error', "Your message is shorter than a tweet from 2006! 📝 Please write a bit more!");
            return false;
        }

        return true;
    };

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!validateForm()) return;
        
        setStatus('sending');

        const formElement = e.target;
        const formData = new FormData(formElement);

        try {
            const response = await fetch('https://formsubmit.co/rahul2000maths@gmail.com', {
                method: 'POST',
                body: formData,
                headers: {
                    'Accept': 'application/json'
                }
            });

            if (response.ok) {
                setStatus('success');
                showPopup('success');
                setFormData({ name: '', email: '', subject: '', message: '' });
            } else {
                throw new Error('Form submission failed');
            }
        } catch (error) {
            console.error('Error sending message:', error);
            setStatus('error');
            showPopup('error');
        } finally {
            setTimeout(() => setStatus(''), 3000);
        }
    };

    const socialLinks = [
        {
            name: 'LinkedIn',
            icon: <Linkedin size={24} />,
            url: 'https://www.linkedin.com/in/rahul-mishra-9a8826269/',
            color: '#0077B5'
        },
        {
            name: 'GitHub',
            icon: <Github size={24} />, 
            url: 'https://github.com/machinelearningprodigy',
            color: '#FFFFFF'
        },
        {
            name: 'Twitter',
            icon: <Twitter size={24} />,
            url: 'https://x.com/NitishM66803568',
            color: '#09F2FC'
        },
        {
            name: 'Instagram',
            icon: <Instagram size={24} />,
            url: 'https://www.instagram.com/rahul9076mishra/',
            color: '#FA00BB'
        }
    ];

    return (
        <div className="contact-container">
            <div className="animated-background">
                {[...Array(20)].map((_, i) => (
                    <div
                        key={i}
                        className="particle"
                        style={{
                            left: `${Math.random() * 100}%`,
                            animationDuration: `${Math.random() * 10 + 5}s`,
                            animationDelay: `${Math.random() * 5}s`,
                            width: `${Math.random() * 8 + 2}px`,
                            height: `${Math.random() * 8 + 2}px`
                        }}
                    />
                ))}
            </div>

            {popup.show && (
                <Popup 
                    type={popup.type}
                    message={popup.message}
                    onClose={() => setPopup({ show: false, type: '', message: '' })}
                />
            )}

            <div className="contact-content">
                <h2 className="section-title">
                    <span className="title-highlight">Get In Touch</span>
                </h2>
                
                <div className="contact-grid">
                    <div className="contact-info">
                        <div className="info-card">
                            <div className="info-icon-wrapper">
                                <Mail className="info-icon" />
                            </div>
                            <h3>Email</h3>
                            <p>rahul2000maths@gmail.com</p>
                        </div>

                        <div className="info-card">
                            <div className="info-icon-wrapper">
                                <Phone className="info-icon" />
                            </div>
                            <h3>Phone</h3>
                            <p>+91 9085471314</p>
                        </div>

                        <div className="info-card">
                            <div className="info-icon-wrapper">
                                <MapPin className="info-icon" />
                            </div>
                            <h3>Location</h3>
                            <p>Guwahati, India</p>
                        </div>

                        <div className="resume-download">
                            <a href="/path-to-resume.pdf" className="download-btn">
                                <FileDown size={20} />
                                <span>Download Resume</span>
                            </a>
                        </div>

                        <div className="social-links">
                            {socialLinks.map((link, index) => (
                                <a
                                    key={index}
                                    href={link.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="social-link"
                                    style={{ '--hover-color': link.color }}
                                >
                                    {link.icon}
                                    <span className="social-tooltip">{link.name}</span>
                                </a>
                            ))}
                        </div>
                    </div>

                    <div className="contact-form-container">
                        <form 
                            onSubmit={handleSubmit} 
                            className="contact-form"
                            action="https://formsubmit.co/rahul2000maths@gmail.com"
                            method="POST"
                        >
                            {/* FormSubmit.io configuration */}
                            <input type="hidden" name="_subject" value="New Contact Form Submission" />
                            <input type="hidden" name="_captcha" value="false" />
                            <input type="hidden" name="_template" value="table" />
                            <input type="hidden" name="_next" />

                            <div className="form-group">
                                <input
                                    type="text"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleChange}
                                    placeholder="Your Name"
                                    required
                                    className="form-input"
                                />
                                <label className="form-label">Your Name</label>
                            </div>

                            <div className="form-group">
                                <input
                                    type="email"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleChange}
                                    placeholder="Your Email"
                                    required
                                    className="form-input"
                                />
                                <label className="form-label">Your Email</label>
                            </div>

                            <div className="form-group">
                                <input
                                    type="text"
                                    name="subject"
                                    value={formData.subject}
                                    onChange={handleChange}
                                    placeholder="Subject"
                                    required
                                    className="form-input"
                                />
                                <label className="form-label">Subject</label>
                            </div>

                            <div className="form-group">
                                <textarea
                                    name="message"
                                    value={formData.message}
                                    onChange={handleChange}
                                    placeholder="Your Message"
                                    required
                                    rows={5}
                                    className="form-input"
                                />
                                <label className="form-label">Your Message</label>
                            </div>

                            <button 
                                type="submit" 
                                className={`submit-btn ${status}`}
                                disabled={status === 'sending'}
                            >
                                {status === 'sending' ? (
                                    <>
                                        <div className="loading-spinner" />
                                        <span>Sending...</span>
                                    </>
                                ) : status === 'success' ? (
                                    <>
                                        <CheckCircle size={20} />
                                        <span>Message Sent!</span>
                                    </>
                                ) : status === 'error' ? (
                                    <>
                                        <AlertTriangle size={20} />
                                        <span>Try Again</span>
                                    </>
                                ) : (
                                    <>
                                        <Send size={20} />
                                        <span>Send Message</span>
                                    </>
                                )}
                            </button>
                        </form>
                    </div>
                </div>

                <div className="map-container">
                    <div className="map-overlay"></div>
                    <iframe
                        title="location"
                        src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d1000!2d91.73195763801392!3d26.15319755492514!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2z26.15319755492514!4d91.73195763801392"
                        width="100%"
                        height="300"
                        style={{ border: 0 }}
                        allowFullScreen=""
                        loading="lazy"
                    />
                </div>
            </div>
        </div>
    );
};

export default Contact;